/**
 * Formatting helpers.
 *
 * Everything that renders a timestamp takes the org's reporting timezone
 * explicitly. Plan D30: the org's timezone decides which day a number belongs
 * to, and a dashboard that silently renders in the viewer's local zone will
 * disagree with the customer's own product about what "yesterday" was.
 */

const compactNumber = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const fullNumber = new Intl.NumberFormat('en');

/** Axis and tile figures. Compact above five digits, exact below. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value);
  return Math.abs(rounded) >= 100000 ? compactNumber.format(rounded) : fullNumber.format(rounded);
}

/** Never compacted. Used wherever the exact figure is the point. */
export function formatExact(value: number): string {
  if (!Number.isFinite(value)) return '0';
  return fullNumber.format(Math.round(value));
}

export function formatPercent(value: number, fractionDigits = 1): string {
  if (!Number.isFinite(value)) return '0%';
  return `${value.toFixed(fractionDigits)}%`;
}

function zoned(timeZone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone });
  } catch {
    // An unknown IANA zone from a stale org record must not throw inside a
    // render. UTC is wrong but visible, and the zone label is always shown
    // next to the value so the mismatch is not silent.
    return new Intl.DateTimeFormat('en-GB', { ...options, timeZone: 'UTC' });
  }
}

/**
 * The viewer's own timezone.
 *
 * A **moment** is rendered here, never a reporting day. The two are different
 * questions and only one of them belongs to the organisation: "which day does
 * this number count towards" is an org decision (D30), because otherwise
 * "yesterday" means two things inside one company. "When did this session
 * start" is not: it is an instant, and the only reader is the person looking at
 * the screen.
 *
 * Conflating them is why a Sydney team read every timestamp ten hours out. The
 * org's reporting timezone is UTC on every workspace created so far, the
 * dashboard passed that zone to the timestamp formatters as well as to the
 * bucket labels, and a session that started at 13:56 local was shown as 03:56
 * with a small "UTC" label elsewhere on the page as the only clue.
 *
 * A reporting timezone cannot be changed once an org has events, because the
 * daily rollups are already bucketed, so this had to be fixed at the point of
 * display rather than by correcting the stored zone.
 */
function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** `14:32` in the viewer's zone, the "as of" stamp of FR-DASH-10. */
export function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return zoned(viewerTimeZone(), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * `7 Aug 2026, 14:32`.
 *
 * `null` is accepted because several timestamps on the read API genuinely are
 * nullable: a person identified before their first event has no `first_seen`,
 * and a live row written by an older collector build has no `received_at`.
 * Rendering "unknown" beside the rest of the row is the honest answer, and it
 * keeps one absent field from blanking a whole table.
 *
 * Shown in the viewer's zone. See :func:`viewerTimeZone`.
 */
export function formatDateTime(iso: string | null): string {
  if (iso === null) return 'unknown';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return zoned(viewerTimeZone(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/**
 * `7 Aug 2026`, in the zone the caller names.
 *
 * Keeps its explicit zone, unlike the two above, because its callers render
 * **reporting days**: the bounds of a range and the labels on a daily chart.
 * Those are org decisions, and relabelling them in the viewer's zone would
 * claim a bucket covers hours it does not.
 */
export function formatDate(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return zoned(timeZone, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

/**
 * Axis tick for a bucket label. The read API returns `YYYY-MM-DD` at day
 * granularity and `YYYY-MM-DDTHH` at hour granularity (plan 5.10), and those
 * labels are already expressed in the correct zone by the server, so they are
 * formatted as written rather than reinterpreted as instants.
 */
/**
 * An hourly bucket key as a UTC instant.
 *
 * The key is `YYYY-MM-DDTHH` and it is UTC by design: plan 4.5 stores hourly
 * counters in UTC and reserves the org's reporting timezone for daily ones, so
 * that an hour means the same thing everywhere. That makes the KEY unambiguous
 * and the LABEL misleading, because it was being sliced out and printed
 * literally: a Sydney reader looking at an afternoon of traffic saw an axis
 * running 00:00 to 07:00, which is when it happened in Greenwich.
 *
 * An hour bucket is an instant, so it can be shown wherever the reader is.
 * Returns null for a key that is not one.
 */
function hourBucketAt(bucket: string): Date | null {
  if (bucket.length < 13) return null;
  const at = new Date(`${bucket.slice(0, 13)}:00:00Z`);
  return Number.isNaN(at.getTime()) ? null : at;
}

export function formatBucketLabel(bucket: string, granularity: 'hour' | 'day'): string {
  if (granularity === 'hour') {
    const at = hourBucketAt(bucket);
    if (at === null) return bucket;
    return zoned(viewerTimeZone(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(at);
  }
  const parts = bucket.split('-');
  const month = parts[1];
  const day = parts[2];
  if (month === undefined || day === undefined) return bucket;
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const name = monthNames[Number(month) - 1];
  return name === undefined ? bucket : `${Number(day)} ${name}`;
}

/** Full bucket label for a tooltip, which has room for the year. */
export function formatBucketTooltip(bucket: string, granularity: 'hour' | 'day'): string {
  if (granularity === 'hour') {
    const at = hourBucketAt(bucket);
    if (at === null) return bucket;
    // Carries the date as well as the hour, because once an hour is shown in
    // the reader's zone it can belong to a different day than the key does.
    return zoned(viewerTimeZone(), {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(at);
  }
  return bucket;
}

/** `6m 52s`, `4h 12m`, `412ms`. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return 'unknown';
  if (seconds < 1) return `${Math.round(seconds * 1000)}ms`;
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

/** Receive latency for the onboarding panel, plan FR-DASH-8. */
export function formatLatency(ms: number): string {
  if (!Number.isFinite(ms)) return 'unknown';
  if (ms < 0) return '0 ms';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(ms < 10000 ? 2 : 1)} s`;
}

/** `just now`, `3 minutes ago`, `2 days ago`. */
export function formatRelative(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return 'unknown';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'unknown';
  const deltaSeconds = Math.round((then - now.getTime()) / 1000);
  const absolute = Math.abs(deltaSeconds);
  if (absolute < 45) return 'just now';

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
  if (absolute < 3600) return formatter.format(Math.round(deltaSeconds / 60), 'minute');
  if (absolute < 86400) return formatter.format(Math.round(deltaSeconds / 3600), 'hour');
  if (absolute < 604800) return formatter.format(Math.round(deltaSeconds / 86400), 'day');
  if (absolute < 2629800) return formatter.format(Math.round(deltaSeconds / 604800), 'week');
  if (absolute < 31557600) {
    return formatter.format(Math.round(deltaSeconds / 2629800), 'month');
  }
  return formatter.format(Math.round(deltaSeconds / 31557600), 'year');
}

/** Whole days remaining, floored, for the key rotation countdown. */
export function daysUntil(iso: string, now: Date = new Date()): number | null {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((then - now.getTime()) / 86400000));
}

/** Stable, readable JSON for the raw event panels. */
export function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2) ?? 'null';
  } catch {
    // Circular structures cannot come from a parsed API response, but a
    // defensive fallback costs nothing and beats a render crash.
    return String(value);
  }
}
